<template>
  <div class="page-container">
    <div class="page-header">
      <h1 class="page-title">{{ pageTitle }}</h1>
      <button v-if="canCreateClient" class="btn btn-primary" @click="handleCreate">
        <i class="ri-add-line"></i>
        新增客戶
      </button>
    </div>

    <div class="page-toolbar">
      <div class="search-box">
        <input
          v-model="searchQuery"
          type="text"
          class="form-control"
          placeholder="搜尋客戶代碼、客戶名稱或平台名稱..."
          @input="handleSearch"
        />
        <i class="ri-search-line"></i>
      </div>
      <div class="filter-box" v-if="isSystemAccount">
        <label>平台篩選：</label>
        <select v-model="selectedPlatformId" @change="handleFilterChange" class="form-control">
          <option value="">全部平台</option>
          <option
            v-for="platform in platforms"
            :key="platform.id"
            :value="platform.id"
          >
            {{ platform.name }} ({{ platform.code }})
          </option>
        </select>
      </div>
      <div class="filter-box">
        <label>狀態篩選：</label>
        <select v-model="selectedStatus" @change="handleFilterChange" class="form-control">
          <option value="">全部狀態</option>
          <option value="active">啟用</option>
          <option value="inactive">停用</option>
          <option value="suspended">暫停</option>
        </select>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchClients">重試</button>
    </div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>平台</th>
            <th>客戶名稱 / 代碼</th>
            <th>訂閱方案</th>
            <th>行業</th>
            <th>選購模組</th>
            <th>狀態</th>
            <th>門市數上限</th>
            <th>會員數上限</th>
            <th>建立時間</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="clients.length === 0">
            <td colspan="10" class="empty-state">
              沒有找到客戶
            </td>
          </tr>
          <tr v-for="client in clients" :key="client.id">
            <td>{{ client.platformName || '-' }}</td>
            <td>
              <span class="client-name">{{ client.clientName }}</span> / {{ client.clientCode }}
            </td>
            <td>
              <span v-if="client.planName" :class="getPlanBadgeClass(client.planCode)">
                {{ client.planName }}
              </span>
              <span v-else class="text-muted">未指定</span>
              <div v-if="client.subscriptionNeverExpires" class="subscription-info">
                <span class="sub-status-dot dot-perpetual"></span>
                <span class="sub-end-date sub-perpetual">永久有效</span>
              </div>
              <div v-else-if="client.subscriptionEndDate" class="subscription-info">
                <span
                  class="sub-status-dot"
                  :class="{
                    'dot-trial': client.subscriptionStatus === 'trial',
                    'dot-active': client.subscriptionStatus === 'active',
                    'dot-expired': client.subscriptionStatus === 'expired' || client.subscriptionStatus === 'grace_period',
                  }"
                ></span>
                <span class="sub-end-date">{{ formatDateOnly(client.subscriptionEndDate) }} 到期</span>
              </div>
            </td>
            <td>{{ client.industry || '-' }}</td>
            <td>
              <div v-if="client.optionalModules && client.optionalModules.length > 0" class="modules-list">
                <span
                  v-for="(module, index) in client.optionalModules"
                  :key="index"
                  class="module-badge"
                  :title="module.moduleName"
                >
                  <i v-if="module.icon" :class="module.icon"></i>
                  {{ module.moduleName }}
                </span>
              </div>
              <span v-else class="text-muted">-</span>
            </td>
            <td>
              <span
                :class="client.status === 'active' ? 'badge badge-success' : 'badge badge-danger'"
              >
                {{ client.status === 'active' ? '啟用' : client.status === 'inactive' ? '停用' : '暫停' }}
              </span>
            </td>
            <td>{{ client.maxStores || '-' }}</td>
            <td>{{ client.maxMembers || '-' }}</td>
            <td>{{ formatDate(client.createdAt) }}</td>
            <td>
              <div v-if="isSystemAccount" class="action-buttons">
                <button
                  class="btn btn-sm btn-secondary"
                  @click="handleEdit(client)"
                  title="編輯"
                >
                  <i class="ri-edit-line"></i>
                </button>
                <button
                  v-if="canCreateClient && client.planId"
                  class="btn btn-sm btn-outline"
                  @click="openDuplicateModal(client)"
                  title="複製客戶"
                >
                  <i class="ri-file-copy-line"></i>
                </button>
                <button
                  v-if="client.planCode === 'TRIAL'"
                  class="btn btn-sm btn-warning"
                  @click="openExtendTrialModal(client)"
                  title="延長試用"
                >
                  <i class="ri-time-line"></i>
                </button>
                <button
                  class="btn btn-sm btn-danger"
                  @click="handleDelete(client)"
                  title="刪除"
                >
                  <i class="ri-delete-bin-line"></i>
                </button>
              </div>
              <div v-else-if="canCreateClient" class="action-buttons">
                <button
                  class="btn btn-sm btn-secondary"
                  @click="handleEdit(client)"
                  title="編輯"
                >
                  <i class="ri-edit-line"></i>
                </button>
                <button
                  v-if="client.planId"
                  class="btn btn-sm btn-outline"
                  @click="openDuplicateModal(client)"
                  title="複製客戶"
                >
                  <i class="ri-file-copy-line"></i>
                </button>
                <span v-else class="text-muted">-</span>
              </div>
              <span v-else class="text-muted">-</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 延長試用 Modal -->
    <div v-if="extendTrialModal.show" class="modal-overlay" @click.self="closeExtendTrialModal">
      <div class="modal-box">
        <div class="modal-header">
          <h3><i class="ri-time-line"></i> 延長試用期</h3>
          <button class="modal-close" @click="closeExtendTrialModal"><i class="ri-close-line"></i></button>
        </div>
        <div class="modal-body">
          <p class="modal-client-name">{{ extendTrialModal.client?.clientName }}</p>
          <p class="modal-desc">目前到期日：<strong>{{ extendTrialModal.currentEndDate || '—' }}</strong></p>
          <div class="modal-field">
            <label>延長天數</label>
            <div class="days-input-wrap">
              <input
                v-model.number="extendTrialModal.days"
                type="number"
                class="form-control"
                min="1"
                max="365"
                placeholder="請輸入天數"
              />
              <span class="days-unit">天</span>
            </div>
            <div class="days-shortcuts">
              <button v-for="d in [7, 14, 30, 60, 90]" :key="d"
                class="days-shortcut-btn"
                :class="{ active: extendTrialModal.days === d }"
                @click="extendTrialModal.days = d"
              >{{ d }} 天</button>
            </div>
          </div>
          <p v-if="extendTrialModal.days > 0" class="modal-new-date">
            延長後到期日：<strong>{{ calcNewEndDate }}</strong>
          </p>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="closeExtendTrialModal" :disabled="extendTrialModal.loading">取消</button>
          <button class="btn btn-primary" @click="confirmExtendTrial" :disabled="!extendTrialModal.days || extendTrialModal.loading">
            <i v-if="extendTrialModal.loading" class="ri-loader-4-line spinning"></i>
            確認延長
          </button>
        </div>
      </div>
    </div>

    <!-- 複製客戶 Modal -->
    <div v-if="duplicateModal.show" class="modal-overlay" @click.self="closeDuplicateModal">
      <div class="modal-box duplicate-modal-box">
        <div class="modal-header">
          <h3><i class="ri-file-copy-line"></i> 複製客戶</h3>
          <button class="modal-close" type="button" @click="closeDuplicateModal"><i class="ri-close-line"></i></button>
        </div>
        <div class="modal-body duplicate-modal-body">
          <p class="modal-client-name">{{ duplicateModal.source?.clientName }}（{{ duplicateModal.source?.clientCode }}）</p>
          <p class="modal-desc">新客戶將為<strong>人工收款</strong>，並<strong>維持與來源相同的訂閱方案</strong>。請設定新客戶代碼與管理員登入。</p>
          <div class="modal-field">
            <label>新客戶代碼 <span class="req">*</span></label>
            <input v-model="duplicateModal.form.clientCode" type="text" class="form-control" maxlength="50" autocomplete="off" />
          </div>
          <div class="modal-field">
            <label>新客戶名稱 <span class="req">*</span></label>
            <input v-model="duplicateModal.form.clientName" type="text" class="form-control" maxlength="100" />
          </div>
          <div class="modal-field">
            <label>登入 Email（同時作為登入帳號） <span class="req">*</span></label>
            <input
              v-model="duplicateModal.form.email"
              type="email"
              class="form-control"
              autocomplete="email"
              @input="syncDuplicateNameFromEmail"
            />
          </div>
          <div class="modal-field">
            <label>帳號名稱（姓名） <span class="req">*</span></label>
            <input v-model="duplicateModal.form.name" type="text" class="form-control" />
          </div>
          <div class="modal-field">
            <label>密碼 <span class="req">*</span></label>
            <input v-model="duplicateModal.form.password" type="password" class="form-control" minlength="8" autocomplete="new-password" />
            <span class="field-hint">至少 8 碼，須含英文字母與符號</span>
          </div>
          <div class="duplicate-checks">
            <label class="check-row"><input type="checkbox" v-model="duplicateModal.form.copyStores" /> 門市</label>
            <label class="check-row"><input type="checkbox" v-model="duplicateModal.form.copyTagsAndAutoTagRules" /> 標籤與自動貼標規則</label>
            <label class="check-row"><input type="checkbox" v-model="duplicateModal.form.copyCouponTemplates" /> 優惠券模板</label>
            <label class="check-row"><input type="checkbox" v-model="duplicateModal.form.copyPointsRulesAndExpiry" /> 點數規則與過期設定</label>
            <label class="check-row"><input type="checkbox" v-model="duplicateModal.form.copyOptionalModules" /> 選購模組（啟用中）</label>
            <label class="check-row"><input type="checkbox" v-model="duplicateModal.form.copyRichMenus" /> 圖文選單（複製為草稿，不自動部署）</label>
            <label class="check-row"><input type="checkbox" v-model="duplicateModal.form.copyMessageTemplates" /> 訊息範本</label>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeDuplicateModal" :disabled="duplicateModal.loading">取消</button>
          <button type="button" class="btn btn-primary" @click="confirmDuplicate" :disabled="duplicateModal.loading || !duplicateFormValid">
            <i v-if="duplicateModal.loading" class="ri-loader-4-line spinning"></i>
            建立複本
          </button>
        </div>
      </div>
    </div>

    <ListPagination
      v-if="total > 0"
      :page="currentPage"
      :total-pages="totalPages"
      :total="total"
      @change="goToPage"
    />
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { clientService } from '@/api/modules/clients.js';
import { platformManagementService } from '@/api/modules/platforms.js';
import { showSuccess, showError, showDeleteConfirm } from '@/utils/swal.js';
import authService from '@/helpers/authservice/authService.js';
import apiClient from '@/api/axios.js';

export default {
  name: 'ClientList',
  setup() {
    const router = useRouter();
    const { pageTitle } = usePageTitle('客戶資料列表');
    const clients = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const searchQuery = ref('');
    const currentPage = ref(1);
    const pageSize = ref(10);
    const total = ref(0);
    const searchTimeout = ref(null);
    const selectedPlatformId = ref('');
    const selectedStatus = ref('');
    const platforms = ref([]);

    const totalPages = computed(() => {
      return Math.ceil(total.value / pageSize.value);
    });

    const isSystemAccount = computed(() => {
      return authService.getAccountType() === 'system';
    });

    const isPlatformAccount = computed(() => {
      return authService.getAccountType() === 'platform';
    });

    const canCreateClient = computed(() => {
      return isSystemAccount.value || isPlatformAccount.value;
    });

    const fetchPlatforms = async () => {
      if (!isSystemAccount.value) return;
      
      try {
        const response = await platformManagementService.getPlatforms({ page: 1, pageSize: 100 });
        if (response && response.success && response.data && response.data.items) {
          platforms.value = response.data.items.map(platform => ({
            id: platform.id,
            name: platform.platformName,
            code: platform.platformCode,
          }));
        }
      } catch (err) {
        console.error('獲取平台列表失敗:', err);
      }
    };

    const fetchClients = async () => {
      loading.value = true;
      error.value = null;

      try {
        const params = {
          page: currentPage.value,
          pageSize: pageSize.value,
        };

        if (searchQuery.value) {
          params.search = searchQuery.value;
        }

        // 系統帳號可以選擇平台
        if (isSystemAccount.value && selectedPlatformId.value) {
          params.platformId = selectedPlatformId.value;
        }

        // 狀態篩選
        if (selectedStatus.value) {
          params.status = selectedStatus.value;
        }

        const response = await clientService.getClients(params);

        if (response && response.success && response.data) {
          clients.value = response.data.items || [];
          total.value = response.data.total || 0;
        } else {
          error.value = response && response.message || '獲取客戶列表失敗';
        }
      } catch (err) {
        console.error('獲取客戶列表失敗:', err);
        error.value = err.message || '獲取客戶列表失敗';
      } finally {
        loading.value = false;
      }
    };

    const handleSearch = () => {
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
      }
      searchTimeout.value = setTimeout(() => {
        currentPage.value = 1;
        fetchClients();
      }, 500);
    };

    const handleFilterChange = () => {
      currentPage.value = 1;
      fetchClients();
    };

    const goToPage = (page) => {
      if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page;
        fetchClients();
      }
    };

    const handleCreate = () => {
      router.push('/system/clients/create');
    };

    const handleEdit = (client) => {
      router.push(`/system/clients/${client.id}/edit`);
    };

    // 延長試用期
    const extendTrialModal = ref({
      show: false,
      client: null,
      currentEndDate: '',
      days: 14,
      loading: false,
    });

    const calcNewEndDate = computed(() => {
      if (!extendTrialModal.value.currentEndDate || !extendTrialModal.value.days) return '';
      const base = new Date(extendTrialModal.value.currentEndDate);
      base.setDate(base.getDate() + extendTrialModal.value.days);
      return base.toLocaleDateString('zh-TW', { year: 'numeric', month: '2-digit', day: '2-digit' });
    });

    const openExtendTrialModal = (client) => {
      extendTrialModal.value = {
        show: true,
        client,
        currentEndDate: client.subscriptionEndDate || '',
        days: 14,
        loading: false,
      };
    };

    const closeExtendTrialModal = () => {
      extendTrialModal.value.show = false;
    };

    const confirmExtendTrial = async () => {
      const { client, days } = extendTrialModal.value;
      if (!client || !days || days < 1) return;

      extendTrialModal.value.loading = true;
      try {
        const resp = await apiClient.post(`/system/clients/${client.id}/extend-trial`, { days });
        if (resp?.success) {
          await showSuccess('延長成功', `試用期已延長 ${days} 天，新到期日：${resp.data?.newEndDate || ''}`);
          closeExtendTrialModal();
          await fetchClients();
        } else {
          await showError('延長失敗', resp?.message || '操作失敗，請稍後再試');
        }
      } catch (err) {
        await showError('延長失敗', err.message || '操作失敗');
      } finally {
        extendTrialModal.value.loading = false;
      }
    };

    const defaultDuplicateForm = () => ({
      clientCode: '',
      clientName: '',
      email: '',
      name: '',
      password: '',
      copyStores: false,
      copyTagsAndAutoTagRules: false,
      copyCouponTemplates: false,
      copyPointsRulesAndExpiry: false,
      copyOptionalModules: false,
      copyRichMenus: false,
      copyMessageTemplates: false,
    });

    const duplicateModal = ref({
      show: false,
      source: null,
      loading: false,
      form: defaultDuplicateForm(),
    });

    const duplicateFormValid = computed(() => {
      const f = duplicateModal.value.form;
      const em = (f.email || '').trim();
      return (
        (f.clientCode || '').trim().length > 0
        && (f.clientName || '').trim().length > 0
        && em.length >= 3
        && (f.name || '').trim().length > 0
        && (f.password || '').length >= 8
      );
    });

    const syncDuplicateNameFromEmail = () => {
      const val = (duplicateModal.value.form.email || '').trim();
      const at = val.indexOf('@');
      if (at > 0) {
        duplicateModal.value.form.name = val.slice(0, at).trim();
      }
    };

    const openDuplicateModal = async (client) => {
      let code = '';
      try {
        const gen = await clientService.generateClientCode(client.platformId);
        if (gen?.success && gen?.data?.clientCode) {
          code = gen.data.clientCode;
        }
      } catch (e) {
        console.warn('產生客戶代碼失敗', e);
      }
      duplicateModal.value = {
        show: true,
        source: client,
        loading: false,
        form: {
          ...defaultDuplicateForm(),
          clientCode: code,
          clientName: `${client.clientName}（複本）`,
        },
      };
    };

    const closeDuplicateModal = () => {
      duplicateModal.value.show = false;
      duplicateModal.value.source = null;
      duplicateModal.value.form = defaultDuplicateForm();
    };

    const confirmDuplicate = async () => {
      const src = duplicateModal.value.source;
      const f = duplicateModal.value.form;
      if (!src || !duplicateFormValid.value) return;

      duplicateModal.value.loading = true;
      try {
        const loginEmail = f.email.trim();
        const resp = await clientService.duplicateClient(src.id, {
          clientCode: f.clientCode.trim(),
          clientName: f.clientName.trim(),
          username: loginEmail,
          password: f.password,
          email: loginEmail,
          name: f.name.trim(),
          copyStores: !!f.copyStores,
          copyTagsAndAutoTagRules: !!f.copyTagsAndAutoTagRules,
          copyCouponTemplates: !!f.copyCouponTemplates,
          copyPointsRulesAndExpiry: !!f.copyPointsRulesAndExpiry,
          copyOptionalModules: !!f.copyOptionalModules,
          copyRichMenus: !!f.copyRichMenus,
          copyMessageTemplates: !!f.copyMessageTemplates,
        });
        if (resp?.success) {
          await showSuccess(
            '複製成功',
            `已建立客戶，方案：${resp.data?.planName || ''}`,
          );
          closeDuplicateModal();
          await fetchClients();
        } else {
          await showError('複製失敗', resp?.message || '操作失敗');
        }
      } catch (err) {
        const msg = err?.data?.message || err?.message || '複製失敗';
        await showError('複製失敗', msg);
      } finally {
        duplicateModal.value.loading = false;
      }
    };

    const handleDelete = async (client) => {
      const confirmed = await showDeleteConfirm(
        `${client.clientName} (${client.clientCode})`,
        [
          '帳號（客戶管理員與所有員工帳號）',
          '會員資料與點數交易記錄',
          'LINE 好友、設定、Rich Menu、廣播訊息',
          '所有標籤、訂單、優惠券、訂閱記錄',
          '門市、簽到、抽獎等所有相關資料',
          '⚠️ 此操作無法復原，請謹慎確認！',
        ]
      );
      
      if (!confirmed) {
        return;
      }

      const originalLoading = loading.value;
      loading.value = true;
      error.value = null;

      try {
        const response = await clientService.deleteClient(client.id);

        if (response && response.success) {
          await showSuccess('刪除成功', `客戶「${client.clientName}」已成功刪除`);
          
          if (clients.value.length === 1 && currentPage.value > 1) {
            currentPage.value = currentPage.value - 1;
          }
          
          await fetchClients();
        } else {
          const errorMsg = response?.message || '刪除客戶失敗';
          await showError('刪除失敗', errorMsg);
        }
      } catch (err) {
        console.error('刪除客戶失敗:', err);
        let errorTitle = '刪除失敗';
        let errorText = '刪除客戶失敗，請稍後再試';
        
        if (err.code === 'NOT_FOUND') {
          errorText = '客戶不存在或已被刪除';
        } else if (err.code === 'FORBIDDEN') {
          errorText = '您沒有權限刪除此客戶';
        } else if (err.response?.status === 404) {
          errorText = '客戶不存在或路由錯誤';
        } else if (err.response?.status === 403) {
          errorText = '您沒有權限刪除此客戶';
        } else if (err.message) {
          errorText = err.message;
        }
        
        await showError(errorTitle, errorText);
      } finally {
        loading.value = originalLoading;
      }
    };

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('zh-TW');
    };

    const formatDateOnly = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('zh-TW', { year: 'numeric', month: '2-digit', day: '2-digit' });
    };

    const getPlanBadgeClass = (planCode) => {
      if (!planCode) return 'badge badge-secondary';
      
      // 根據方案代碼分配不同顏色
      const planCodeLower = planCode.toLowerCase();
      if (planCodeLower.includes('basic') || planCodeLower.includes('標準') || planCodeLower.includes('基礎')) {
        return 'badge badge-info';
      } else if (planCodeLower.includes('standard') || planCodeLower.includes('標準')) {
        return 'badge badge-primary';
      } else if (planCodeLower.includes('premium') || planCodeLower.includes('進階') || planCodeLower.includes('高級')) {
        return 'badge badge-purple';
      } else if (planCodeLower.includes('enterprise') || planCodeLower.includes('企業')) {
        return 'badge badge-orange';
      } else if (planCodeLower.includes('pro') || planCodeLower.includes('專業')) {
        return 'badge badge-warning';
      } else {
        // 使用 planCode 的 hash 來分配顏色，確保相同方案總是相同顏色
        const colors = ['badge-info', 'badge-primary', 'badge-purple', 'badge-orange', 'badge-warning'];
        let hash = 0;
        for (let i = 0; i < planCode.length; i++) {
          hash = planCode.charCodeAt(i) + ((hash << 5) - hash);
        }
        const colorIndex = Math.abs(hash) % colors.length;
        return `badge ${colors[colorIndex]}`;
      }
    };

    onMounted(() => {
      fetchPlatforms();
      fetchClients();
    });

    return {
      clients,
      loading,
      error,
      searchQuery,
      currentPage,
      totalPages,
      total,
      selectedPlatformId,
      selectedStatus,
      platforms,
      isSystemAccount,
      isPlatformAccount,
      canCreateClient,
      pageTitle,
      handleSearch,
      handleFilterChange,
      goToPage,
      handleCreate,
      handleEdit,
      handleDelete,
      formatDate,
      formatDateOnly,
      getPlanBadgeClass,
      fetchClients,
      extendTrialModal,
      calcNewEndDate,
      openExtendTrialModal,
      closeExtendTrialModal,
      confirmExtendTrial,
      duplicateModal,
      duplicateFormValid,
      openDuplicateModal,
      closeDuplicateModal,
      confirmDuplicate,
      syncDuplicateNameFromEmail,
    };
  },
};
</script>

<style scoped>
@import '~@/assets/scss/components/_table-common.scss';
@import '~@/assets/scss/components/_list-page-common.scss';

.page-toolbar {
  flex-wrap: wrap;
}

.table-wrapper table {
  min-width: 1200px; /* 客戶列表有 10 個欄位 */
}

/* 模組列表樣式 */
.modules-list {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.module-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 6px;
  background: #f0f0f0;
  border-radius: 4px;
  font-size: 11px;
  color: #555;
  white-space: nowrap;
}

.module-badge i {
  font-size: 12px;
}

.text-muted {
  color: #999;
  font-style: italic;
}

/* 篩選器樣式 */
.filter-box {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

.filter-box label {
  white-space: nowrap;
  font-size: 0.875rem;
  color: #666;
  min-width: fit-content;
}

.filter-box select {
  width: 200px;
  min-width: 150px;
  max-width: 250px;
}

.client-name {
  color: #2563eb;
  font-weight: 500;
}

.subscription-info {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
}

.sub-status-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}

.dot-trial  { background: #f59e0b; }
.dot-active { background: #10b981; }
.dot-expired { background: #ef4444; }
.dot-perpetual { background: #2563eb; }

.sub-perpetual {
  color: #2563eb;
  font-weight: 600;
}

.sub-end-date {
  font-size: 11px;
  color: #888;
  white-space: nowrap;
}

/* 延長試用 Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.modal-box {
  background: #fff;
  border-radius: 12px;
  width: 420px;
  max-width: 92vw;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  overflow: hidden;
}

.duplicate-modal-box {
  width: 520px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.duplicate-modal-body {
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.req {
  color: #dc2626;
}

.field-hint {
  display: block;
  font-size: 11px;
  color: #888;
  margin-top: 4px;
}

.duplicate-checks {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid #eee;
}

.check-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #444;
  cursor: pointer;
}

.check-row input {
  width: 16px;
  height: 16px;
}

.action-buttons .btn-outline {
  background: transparent;
  border: 1px solid #64748b;
  color: #475569;
}

.action-buttons .btn-outline:hover {
  background: #f1f5f9;
  border-color: #475569;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
}

.modal-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
  color: #333;
}

.modal-close {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 20px;
  color: #999;
  padding: 2px;
  line-height: 1;
}

.modal-body {
  padding: 20px;
}

.modal-client-name {
  font-size: 15px;
  font-weight: 600;
  color: #2563eb;
  margin: 0 0 6px;
}

.modal-desc {
  color: #666;
  font-size: 13px;
  margin: 0 0 16px;
}

.modal-field label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #555;
  margin-bottom: 8px;
}

.days-input-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
}

.days-input-wrap input {
  flex: 1;
}

.days-unit {
  color: #666;
  font-size: 14px;
  white-space: nowrap;
}

.days-shortcuts {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 10px;
}

.days-shortcut-btn {
  padding: 4px 10px;
  border: 1px solid #d1d5db;
  border-radius: 16px;
  background: #f9fafb;
  cursor: pointer;
  font-size: 12px;
  color: #555;
  transition: all 0.15s;
}

.days-shortcut-btn:hover,
.days-shortcut-btn.active {
  background: #2563eb;
  border-color: #2563eb;
  color: #fff;
}

.modal-new-date {
  margin-top: 14px;
  padding: 10px 12px;
  background: #f0f7ff;
  border-radius: 8px;
  font-size: 13px;
  color: #444;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid #f0f0f0;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.spinning {
  display: inline-block;
  animation: spin 0.8s linear infinite;
  margin-right: 4px;
}

/* 響應式設計 - 手機版 */
@media (max-width: 768px) {
  .page-toolbar {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .search-box {
    flex: 1;
    max-width: 100%;
    width: 100%;
  }

  .filter-box {
    width: 100%;
  }

  .filter-box select {
    width: 100%;
  }
}
</style>
